/* ============================================================
   AI HERO — Concierge Chat
   Arrábida Untold | ai-chat.js
   v2.1 — 2026-04-06
   ============================================================
   Alterações v2.1:
   - Quando %%SEND_PLAN%% detetado: mostra confirmação curta em vez do plano completo
   - Mensagem de confirmação i18n (PT/EN/ES/FR) com contacto travel designer

   Alterações v2.0:
   - session_id gerado em localStorage e enviado em cada POST
   - lang removido do POST body (Claude deteta automaticamente)
   - lang mantido apenas para welcome message e placeholder
   - Mecanismo %%SEND_PLAN%% removido (tratado server-side)
   - Streaming progressivo: tokens aparecem à medida que chegam
   - Suporte a FR adicionado
   - appendSystemMessage com i18n
   ============================================================ */

(function () {
  'use strict';

  // ── Session ID ──────────────────────────────────────────────
  // Persiste em localStorage para associar mensagens no Supabase.
  // Gerado uma vez por browser/dispositivo.
  function getSessionId() {
    const KEY = 'au_session_id';
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(KEY, id);
    }
    return id;
  }

  const SESSION_ID = getSessionId();

  // ── Language detection ──────────────────────────────────────
  // Usado APENAS para welcome message e placeholder.
  // O Claude deteta a língua das mensagens automaticamente.
  function detectLang() {
    const shopifyLocale = (
      (window.Shopify && window.Shopify.locale) ||
      document.documentElement.lang ||
      ''
    ).toLowerCase();
    if (shopifyLocale.startsWith('pt')) return 'pt';
    if (shopifyLocale.startsWith('es')) return 'es';
    if (shopifyLocale.startsWith('fr')) return 'fr';

    const browserLang = (navigator.language || navigator.userLanguage || 'en').toLowerCase();
    if (browserLang.startsWith('pt')) return 'pt';
    if (browserLang.startsWith('es')) return 'es';
    if (browserLang.startsWith('fr')) return 'fr';

    return 'en';
  }

  // ── DOM references ──────────────────────────────────────────
  const chatEl = document.getElementById('ai-chat');
  if (!chatEl) return;

  const messagesEl = document.getElementById('ai-chat-messages');
  const inputEl    = document.getElementById('ai-chat-input');
  const sendBtn    = document.getElementById('ai-chat-send');

  // ── Config from section settings ────────────────────────────
  const WORKER_URL = chatEl.dataset.workerUrl || '';
  const lang       = detectLang();

  const WELCOME = {
    en: chatEl.dataset.welcomeEn,
    pt: chatEl.dataset.welcomePt,
    es: chatEl.dataset.welcomeEs,
    fr: chatEl.dataset.welcomeFr || chatEl.dataset.welcomeEn, // fallback EN se FR não configurado
  };

  const PLACEHOLDER = {
    en: chatEl.dataset.placeholderEn,
    pt: chatEl.dataset.placeholderPt,
    es: chatEl.dataset.placeholderEs,
    fr: chatEl.dataset.placeholderFr || chatEl.dataset.placeholderEn,
  };

  // Mensagens de sistema (i18n)
  const SYSTEM_MSGS = {
    planSent: {
      en: (email) => `Plan sent to ${email}`,
      pt: (email) => `Plano enviado para ${email}`,
      es: (email) => `Plan enviado a ${email}`,
      fr: (email) => `Plan envoyé à ${email}`,
    },
    error: {
      en: 'Sorry, something went wrong. Please try again or contact us directly.',
      pt: 'Desculpe, ocorreu um erro. Por favor tente novamente ou contacte-nos.',
      es: 'Lo sentimos, algo salió mal. Inténtelo de nuevo o contáctenos.',
      fr: 'Désolé, une erreur est survenue. Veuillez réessayer ou nous contacter.',
    },
  };

  let isStreaming = false;

  // ── Init ────────────────────────────────────────────────────
  function init() {
    inputEl.placeholder = PLACEHOLDER[lang] || PLACEHOLDER.en;

    setTimeout(() => {
      appendMessage('assistant', WELCOME[lang] || WELCOME.en);
    }, 600);

    inputEl.addEventListener('input', onInput);
    inputEl.addEventListener('keydown', onKeyDown);
    sendBtn.addEventListener('click', sendMessage);

    const hero = document.getElementById('ai-hero');
    if (hero) {
      requestAnimationFrame(() => hero.classList.add('loaded'));
    }
  }

  // ── Input handlers ──────────────────────────────────────────
  function onInput() {
    inputEl.style.height = 'auto';
    inputEl.style.height = Math.min(inputEl.scrollHeight, 120) + 'px';
    sendBtn.disabled = inputEl.value.trim().length === 0 || isStreaming;
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!sendBtn.disabled) sendMessage();
    }
  }

  // ── Send message ────────────────────────────────────────────
  async function sendMessage() {
    const text = inputEl.value.trim();
    if (!text || isStreaming) return;

    inputEl.value = '';
    inputEl.style.height = 'auto';
    sendBtn.disabled = true;
    isStreaming = true;

    appendMessage('user', text);

    // Reserva um elemento para a resposta do assistente (streaming progressivo)
    const assistantEl = appendMessageStreaming('assistant');
    let fullText = '';

    try {
      await streamResponse(text, assistantEl, (chunk) => {
        fullText += chunk;
        assistantEl.querySelector('.ai-msg__bubble').innerHTML = formatText(fullText);
        requestAnimationFrame(() => {
          messagesEl.scrollTop = messagesEl.scrollHeight;
        });
      });

      // Detetar marcador %%SEND_PLAN%% — mostrar confirmação curta (plano enviado server-side)
      const planMarkerMatch = fullText.match(/%%SEND_PLAN:(\{[^%]+\})%%/);
      if (planMarkerMatch) {
        let planData = {};
        try { planData = JSON.parse(planMarkerMatch[1]); } catch {}
        const clientName  = planData.name  || '';
        const clientEmail = planData.email || '';
        // Usa a língua da conversa (enviada pelo Claude no marcador) — fallback para lang do browser
        const confirmedLang = planData.lang || lang;

        // Confirmação curta i18n — substitui todo o texto da mensagem
        const CONFIRM = {
          pt: `Enviado!${clientName ? ' Boa viagem, ' + clientName + '.' : ' Boa viagem.'} Se tiveres alguma questão adicional, é só dizer ou contactar os nossos travel designers em concierge@arrabidauntold.com.`,
          en: `Sent!${clientName ? ' Enjoy your trip, ' + clientName + '!' : ' Enjoy your trip!'} For any questions, feel free to ask or reach our travel designers at concierge@arrabidauntold.com.`,
          es: `¡Enviado!${clientName ? ' ¡Buen viaje, ' + clientName + '!' : ' ¡Buen viaje!'} Para cualquier duda, escríbenos o contacta a nuestros travel designers en concierge@arrabidauntold.com.`,
          fr: `Envoyé !${clientName ? ' Bon voyage, ' + clientName + ' !' : ' Bon voyage !'} Pour toute question, écrivez-nous ou contactez nos travel designers à concierge@arrabidauntold.com.`,
        };
        const confirmMsg = CONFIRM[confirmedLang] || CONFIRM.en;
        assistantEl.querySelector('.ai-msg__bubble').innerHTML = formatText(confirmMsg);

        if (clientEmail) {
          appendSystemMessage((SYSTEM_MSGS.planSent[confirmedLang] || SYSTEM_MSGS.planSent.en)(clientEmail));
        }
      }

    } catch (err) {
      assistantEl.remove();
      appendError(lang);
      console.error('[AI Chat] Error:', err);
    }

    isStreaming = false;
    sendBtn.disabled = inputEl.value.trim().length === 0;
    inputEl.focus();
  }

  // ── POST para o Worker ──────────────────────────────────────
  // Envia apenas a mensagem atual + session_id.
  // O worker vai buscar o histórico ao Supabase.
  // lang NÃO é enviado — o Claude deteta automaticamente.
  async function streamResponse(message, assistantEl, onChunk) {
    if (!WORKER_URL || WORKER_URL.includes('YOUR_WORKER')) {
      onChunk('[Demo] Configure o URL do Cloudflare Worker nas definições da secção.');
      return;
    }

    const res = await fetch(WORKER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message,
        session_id: SESSION_ID,
      }),
    });

    if (!res.ok) {
      throw new Error(`Worker returned ${res.status}`);
    }

    const contentType = res.headers.get('content-type') || '';

    if (contentType.includes('text/event-stream')) {
      await readStream(res.body, onChunk);
      return;
    }

    // Fallback JSON
    const data = await res.json();
    const text = data?.reply || data?.content?.[0]?.text || data?.text || '';
    onChunk(text);
  }

  // ── SSE stream reader — progressivo ────────────────────────
  async function readStream(body, onChunk) {
    const reader  = body.getReader();
    const decoder = new TextDecoder();
    let buffer    = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop(); // guarda linha incompleta

      for (const line of lines) {
        if (!line.startsWith('data:')) continue;
        const raw = line.slice(5).trim();
        if (raw === '[DONE]') continue;

        try {
          const json = JSON.parse(raw);
          // Suporta formato Anthropic streaming
          const delta = json?.delta?.text || json?.content_block?.text || '';
          if (delta) onChunk(delta);
        } catch {
          // ignora linhas não-JSON
        }
      }
    }
  }

  // ── DOM helpers ─────────────────────────────────────────────
  function appendMessage(role, text) {
    const msgEl = document.createElement('div');
    msgEl.className = `ai-msg ai-msg--${role}`;

    const bubble = document.createElement('div');
    bubble.className = 'ai-msg__bubble';
    bubble.innerHTML = formatText(text);

    msgEl.appendChild(bubble);
    messagesEl.appendChild(msgEl);
    messagesEl.classList.add('has-messages');

    requestAnimationFrame(() => {
      messagesEl.scrollTop = messagesEl.scrollHeight;
    });

    return msgEl;
  }

  // Cria elemento vazio para preencher progressivamente durante stream
  function appendMessageStreaming(role) {
    const msgEl = document.createElement('div');
    msgEl.className = `ai-msg ai-msg--${role}`;

    const bubble = document.createElement('div');
    bubble.className = 'ai-msg__bubble';
    bubble.innerHTML = `
      <span class="ai-typing-dot"></span>
      <span class="ai-typing-dot"></span>
      <span class="ai-typing-dot"></span>
    `;

    msgEl.appendChild(bubble);
    messagesEl.appendChild(msgEl);
    messagesEl.classList.add('has-messages');

    requestAnimationFrame(() => {
      messagesEl.scrollTop = messagesEl.scrollHeight;
    });

    return msgEl;
  }

  function appendError(lang) {
    const msgEl = document.createElement('div');
    msgEl.className = 'ai-msg ai-msg--assistant ai-msg--error';

    const bubble = document.createElement('div');
    bubble.className = 'ai-msg__bubble';
    bubble.textContent = SYSTEM_MSGS.error[lang] || SYSTEM_MSGS.error.en;

    msgEl.appendChild(bubble);
    messagesEl.appendChild(msgEl);
    messagesEl.classList.add('has-messages');
  }

  function appendSystemMessage(text) {
    const msgEl = document.createElement('div');
    msgEl.className = 'ai-msg ai-msg--system';
    msgEl.style.cssText = 'text-align:center;margin:8px 16px;';

    const bubble = document.createElement('div');
    bubble.className = 'ai-msg__bubble';
    bubble.style.cssText = 'display:inline-block;background:rgba(200,169,110,0.15);color:#c8a96e;border:1px solid rgba(200,169,110,0.3);border-radius:20px;padding:6px 14px;font-size:12px;font-family:sans-serif;';
    bubble.textContent = text;

    msgEl.appendChild(bubble);
    messagesEl.appendChild(msgEl);
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  // ── Text formatting ─────────────────────────────────────────
  function formatText(text) {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>')
      .replace(/\n/g, '<br>');
  }

  // ── Start ───────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
